import React, { useEffect, useMemo, useState } from 'react';
import useGetPackaging from '../../../api/packaging/useGetPackaging';
import InputCombobox from '../../../components/agv-forms-combobox/input-combobox';
import InputText from '../../../components/agv-forms-input';
import InputRadio from '../../../components/agv-forms-radio';
import TextArea from '../../../components/agv-forms-textarea';
import GenericModal from '../../../components/agv-generic-modal';
import {
  CONSIGNMENT_ADJUSTMENT_COMMENT_MAX_LENGTH,
  CONSIGNMENT_ADJUSTMENT_DIRECTION,
  formatConsignmentStockValue,
} from '../../../utils/consignment.util';

const PACKAGING_FILTERS = { limit: 200, offset: 0, query: '' };

/**
 * Modale d'ajustement manuel du stock de palettes consignées d'un destinataire (FLEETC-3277).
 * Type de palette, sens (ajout / retrait), quantité, motif obligatoire,
 * et aperçu du stock résultant avant validation.
 */
export default function AddressConsignmentAdjustmentModal({
  isOpen,
  setIsOpen,
  stocks,
  createAdjustment,
  mainActionIsLoading,
}) {
  const [packagingId, setPackagingId] = useState(null);
  const [direction, setDirection] = useState(
    CONSIGNMENT_ADJUSTMENT_DIRECTION.ADD
  );
  const [quantity, setQuantity] = useState('');
  const [comment, setComment] = useState('');

  const { data: packagingData } = useGetPackaging(PACKAGING_FILTERS, 0);

  useEffect(() => {
    if (!isOpen) {
      setPackagingId(null);
      setDirection(CONSIGNMENT_ADJUSTMENT_DIRECTION.ADD);
      setQuantity('');
      setComment('');
    }
  }, [isOpen]);

  const packagingOptions = useMemo(
    () =>
      (packagingData?.items ?? [])
        .filter((item) => item.is_active)
        .map((item) => ({
          value: item.id,
          name: `${item.label} (${item.code})`,
        })),
    [packagingData]
  );

  const parsedQuantity = Number(quantity);
  const isQuantityValid =
    quantity !== '' && Number.isInteger(parsedQuantity) && parsedQuantity > 0;
  const signedQuantity =
    direction === CONSIGNMENT_ADJUSTMENT_DIRECTION.REMOVE
      ? -parsedQuantity
      : parsedQuantity;

  const currentStock =
    stocks?.find((s) => s.packaging_id === packagingId)?.stock ?? 0;
  const resultingStock = currentStock + signedQuantity;

  const isFormValid = !!packagingId && isQuantityValid && !!comment.trim();

  const handleSubmit = () => {
    if (!isFormValid) return;
    createAdjustment({
      packagingId,
      quantity: signedQuantity,
      comment: comment.trim(),
    });
  };

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={'Ajuster le stock de palettes consignées'}
      mainAction={handleSubmit}
      mainActionText={'Valider l’ajustement'}
      mainActionIsLoading={mainActionIsLoading}
      mainActionDisabled={!isFormValid}
    >
      <div className="tw-flex tw-flex-col">
        <InputCombobox
          value={packagingId}
          setValue={setPackagingId}
          fieldTitle={'Type de palette'}
          isRequired
          emptyError={`Le champ "Type de palette" ne peut être vide.`}
          placeholder={'Sélectionner un type de palette'}
          items={packagingOptions}
        />

        <InputRadio
          value={direction}
          setValue={setDirection}
          fieldTitle={'Sens'}
          items={[
            { name: 'Ajout', value: CONSIGNMENT_ADJUSTMENT_DIRECTION.ADD },
            { name: 'Retrait', value: CONSIGNMENT_ADJUSTMENT_DIRECTION.REMOVE },
          ]}
          style={'tw-mt-4'}
        />

        <InputText
          value={quantity}
          setValue={setQuantity}
          fieldTitle={'Quantité'}
          type={'number'}
          isRequired
          emptyError={`Le champ "Quantité" ne peut être vide.`}
          isBadFormat={quantity !== '' && !isQuantityValid}
          placeholder={'Ex : 3'}
          style={'tw-mt-4'}
        />

        <TextArea
          value={comment}
          setValue={setComment}
          fieldTitle={'Motif'}
          isRequired
          emptyError={`Le motif de l'ajustement est obligatoire.`}
          placeholder={'Ex : inventaire chez le client, palettes cassées…'}
          maxLength={CONSIGNMENT_ADJUSTMENT_COMMENT_MAX_LENGTH}
          style={'tw-mt-4'}
        />

        {packagingId && isQuantityValid && (
          <div
            data-testid="consignment-adjustment-preview"
            className="tw-mt-4 tw-rounded-lg tw-border tw-border-solid tw-border-[#dfe3e6] tw-bg-[#f6f7f8] tw-px-3 tw-py-2.5 tw-text-[13px] tw-font-work"
          >
            <div className="tw-flex tw-items-center tw-justify-between">
              <span className="tw-text-[rgba(0,0,0,0.55)]">Stock actuel</span>
              <span>{formatConsignmentStockValue(currentStock)}</span>
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-mt-1">
              <span className="tw-text-[rgba(0,0,0,0.55)]">Ajustement</span>
              <span className="tw-font-bold">
                {signedQuantity > 0 ? `+${signedQuantity}` : signedQuantity}
              </span>
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-mt-1 tw-pt-1 tw-border-solid tw-border-0 tw-border-t tw-border-[#dfe3e6]">
              <span className="tw-text-[rgba(0,0,0,0.90)]">
                Stock après ajustement
              </span>
              <span>{formatConsignmentStockValue(resultingStock)}</span>
            </div>
            {resultingStock < 0 && (
              <p className="tw-text-xs tw-text-amber-800 tw-mt-2 tw-mb-0">
                Le stock résultant est négatif : il apparaîtra comme une
                anomalie à réconcilier.
              </p>
            )}
          </div>
        )}
      </div>
    </GenericModal>
  );
}
